/**
 * Motherboard Raw Model
 *
 * TypeScript interface for the raw motherboard schema (v2.0.0)
 * Used for detail files loaded from public/data/motherboard/details/
 *
 * This schema is comprehensive and includes all fields from manufacturer specs.
 * For list views, use MotherboardListItem from motherboard-list-model.ts
 */

// ============================================================
// Schema Version
// ============================================================

export type SchemaVersion = string // e.g. "2.0.0"

// ============================================================
// Basic Types
// ============================================================

export interface ManufacturerInfo {
  name: string
  country?: string
  website?: string
  model?: string
  series?: string
}

export type MotherboardFormFactor =
  | 'E-ATX'
  | 'ATX'
  | 'Micro-ATX'
  | 'Mini-ITX'
  | 'SSI-CEB'
  | 'SSI-EEB'
  | 'DTX'
  | 'Mini-DTX'
  | 'STX'
  | 'Thin Mini-ITX'

export interface Chipset {
  name: string
  manufacturer: string
  revision?: string
}

export interface Dimensions {
  widthMm?: number
  heightMm?: number
  depthMm?: number
  unit?: 'mm' | 'in'
  raw?: string
}

// ============================================================
// Memory Specification
// ============================================================

export type MemoryType = 'DDR4' | 'DDR5' | 'LPDDR4' | 'LPDDR5' | 'DDR3' | 'DDR2'

export type MemoryProfile = 'XMP' | 'EXPO' | 'A-XMP' | 'AEMP' | 'DOCP'

export interface MemorySpec {
  type: MemoryType
  maxCapacityGb: number
  slots: number
  maxSpeedMhz?: number
  eccSupport: boolean
  profileSupport: MemoryProfile[]
  notes?: string
}

// ============================================================
// PCIe Specification
// ============================================================

export type PcieProtocol = 'PCIe 5.0' | 'PCIe 4.0' | 'PCIe 3.0' | 'PCIe 2.0'

export type PcieSize = 'x16' | 'x8' | 'x4' | 'x1'

export interface PcieSlot {
  type?: string
  size: PcieSize
  lanes: number
  supportedProtocol?: PcieProtocol
  protocol?: string // Legacy field
  isPrimary: boolean
  notes?: string
}

// ============================================================
// Storage Specification
// ============================================================

export type M2Location = 'CPU-attached' | 'Chipset-attached' | 'Front' | 'Rear'

export type M2Key = 'M' | 'E' | 'B' | 'A'

export type M2Size = '2230' | '2242' | '2260' | '2280' | '22110'

export interface M2Slot {
  location: M2Location
  supportedProtocols: ('NVMe' | 'SATA')[]
  key: M2Key
  size: M2Size[]
  maxPcieLanes: number | null
  maxPcieProtocol?: PcieProtocol
  notes?: string
}

export type RaidTechnology = 'AMD RAIDXpert2' | 'Intel RST' | 'Intel VROC'

export interface RaidSpec {
  sata: boolean
  nvme: boolean
  technology?: RaidTechnology
  notes?: string
}

export interface StorageSpec {
  sataPorts: number
  m2Slots: M2Slot[]
  u2Ports?: number
  raid?: RaidSpec
  notes?: string
}

// ============================================================
// Network Specification
// ============================================================

export type LanSpeed = 0.1 | 1 | 2.5 | 5 | 10

export interface LanPort {
  speedGbps: LanSpeed
  controller: string
  notes?: string
}

export type WifiStandard = 'Wi-Fi 6' | 'Wi-Fi 6E' | 'Wi-Fi 7' | 'Wi-Fi 5' | 'Wi-Fi 4'

export interface WifiSpec {
  standard: WifiStandard
  controller: string
  notes?: string
}

export type BluetoothVersion = '5.4' | '5.3' | '5.2' | '5.1' | '5.0' | '4.2' | '4.1' | '4.0'

export interface BluetoothSpec {
  version: BluetoothVersion
  notes?: string
}

export interface NetworkSpec {
  lanPorts: LanPort[]
  wifi?: WifiSpec
  bluetooth?: BluetoothSpec
  notes?: string
}

// ============================================================
// Audio Specification
// ============================================================

export type AudioChannels = '2-channel' | '5.1-channel' | '7.1-channel'

export interface AudioSpec {
  codec: string
  channels: AudioChannels
  notes?: string
}

// ============================================================
// USB Specification
// ============================================================

export interface UsbSpec {
  totalUsb2?: number
  totalUsb3p0?: number // 5Gbps
  totalUsb3p1?: number // 10Gbps
  totalUsb3p2?: number // 20Gbps
  notes?: string
}

// ============================================================
// Thunderbolt Specification
// ============================================================

export type ThunderboltVersion = 'Thunderbolt 3' | 'Thunderbolt 4' | 'Thunderbolt 5'

export type ThunderboltSpeed = '20Gbps' | '40Gbps' | '80Gbps' | '120Gbps'

export type ThunderboltLocation = 'Rear' | 'Front' | 'Internal Header'

export interface ThunderboltPort {
  version: ThunderboltVersion
  location: ThunderboltLocation
  speed: ThunderboltSpeed
  features?: {
    displayPort: boolean
    powerDelivery?: string
    daisyChain?: string
    usb4Compatible: boolean
  }
  notes?: string
}

export interface ThunderboltSpec {
  ports: ThunderboltPort[]
}

// ============================================================
// Internal Headers Specification
// ============================================================

export type AudioConnectorType =
  | 'HD Audio'
  | "AC'97"
  | 'Headphone'
  | 'Microphone'
  | 'Combo'
  | 'Optical'
  | 'Coaxial'

export interface RgbHeaderSpec {
  argbHeaders?: number
  rgbHeaders?: number
  notes?: string
}

export interface InternalHeaderSpec {
  usb2Headers: number
  usb3Headers: number
  usbCHeaders?: number
  audioHeader?: AudioConnectorType
  fanHeaders: number
  rgb?: RgbHeaderSpec
  /** @deprecated Use rgb.argbHeaders instead */
  argbHeaders?: number
  /** @deprecated Use rgb.rgbHeaders instead */
  rgbHeaders?: number
  comPortHeaders?: number
  tpmHeaders?: number
  notes?: string
}

// ============================================================
// Rear I/O Specification
// ============================================================

export type UsbConnectorType = 'Type-A' | 'Type-C'

export type UsbSpeed = '2.0' | '5Gbps' | '10Gbps' | '20Gbps' | '40Gbps'

export interface RearUsbPort {
  type: UsbConnectorType
  speed: UsbSpeed
  count: number
  notes?: string
}

export type DisplayPortType =
  | 'HDMI 2.1'
  | 'HDMI 2.0'
  | 'HDMI 1.4'
  | 'DisplayPort 1.4'
  | 'DisplayPort 2.1'
  | 'DVI'
  | 'VGA'

export type AudioJackType =
  | 'Line In'
  | 'Line Out'
  | 'Mic'
  | 'Optical S/PDIF Out'
  | 'Optical S/PDIF In'
  | 'Center/Subwoofer'

export interface RearLanPort {
  speedGbps: LanSpeed
  count: number
  notes?: string
}

export interface RearIOSpec {
  usbPorts: RearUsbPort[]
  displayPorts: DisplayPortType[]
  audioJacks: AudioJackType[]
  lanPorts: RearLanPort[]
  wifiAntennaPorts: boolean
  ps2Ports: boolean
  notes?: string
}

// ============================================================
// BIOS Specification
// ============================================================

export type BiosType = 'UEFI' | 'AMI BIOS' | 'Award BIOS'

export type FlashBackMethod =
  | 'USB BIOS FlashBack'
  | 'Q-Flash Plus'
  | 'Instant Flash'
  | 'SMART BIOS UPDATE Button'
  | 'Flash BIOS Button'

export interface BiosSpec {
  type: BiosType
  romSizeMb: number
  features: string[]
  flashBack?: {
    supported: boolean
    method?: FlashBackMethod
  }
  notes?: string
}

// ============================================================
// VRM Specification
// ============================================================

export interface VrmSpec {
  phaseCount: number
  powerStageAmps?: number
  configuration?: string
  notes?: string
}

// ============================================================
// Power Specification
// ============================================================

export type AtxConnector = '24-pin' | '20-pin'

export type CpuConnector = '8-pin' | '4-pin' | '8-pin + 4-pin' | '8-pin + 8-pin'

export interface PowerSpec {
  atxConnector: AtxConnector
  cpuConnector: CpuConnector
  auxConnectors?: string[]
  recommendedPSU?: string
  notes?: string
}

// ============================================================
// Main Motherboard Raw Model
// ============================================================

export interface MotherboardRawModel {
  // Schema version
  schemaVersion?: SchemaVersion

  // Identity
  id: string
  manufacturer: ManufacturerInfo
  productName: string
  modelNumber?: string
  series?: string
  officialUrl: string

  /** Amazon ASIN */
  asin?: string

  // Core specs
  formFactor: MotherboardFormFactor
  chipset: Chipset
  socket: string

  // Memory
  memory: MemorySpec

  // Expansion
  pcieSlots?: PcieSlot[]
  totalExpansionSlots?: number

  // Storage
  storage?: StorageSpec

  // Connectivity
  network?: NetworkSpec
  audio?: AudioSpec
  usb?: UsbSpec
  thunderbolt?: ThunderboltSpec

  // Internal
  internalHeaders?: InternalHeaderSpec
  rearIO?: RearIOSpec

  // System
  bios?: BiosSpec
  vrm?: VrmSpec
  power?: PowerSpec

  // Physical
  dimensions?: Dimensions

  // Metadata
  releaseDate?: {
    year: string
    month: string
  }
}

// ============================================================
// Type Guards
// ============================================================

/**
 * Type guard to check if an object is a valid MotherboardRawModel
 */
export function isMotherboardRawModel(obj: unknown): obj is MotherboardRawModel {
  if (typeof obj !== 'object' || obj === null) return false

  const item = obj as Record<string, unknown>

  return (
    typeof item.id === 'string' &&
    typeof item.productName === 'string' &&
    typeof item.officialUrl === 'string' &&
    typeof item.formFactor === 'string' &&
    typeof item.socket === 'string' &&
    typeof item.manufacturer === 'object' &&
    item.manufacturer !== null &&
    typeof (item.manufacturer as Record<string, unknown>).name === 'string' &&
    typeof item.chipset === 'object' &&
    item.chipset !== null &&
    typeof (item.chipset as Record<string, unknown>).name === 'string' &&
    typeof item.memory === 'object' &&
    item.memory !== null
  )
}

// ============================================================
// Data Container
// ============================================================

export interface MotherboardRawData {
  items: MotherboardRawModel[]
  metadata: {
    scrapedAt: string
    version: SchemaVersion
    source?: string
  }
}
